import {
    CreateInputOffer,
    CreateInputOfferVariables,
} from "_graphql/mutation/__generated__/CreateInputOffer";
import {
    CreateKEKInputOffer,
    CreateKEKInputOfferVariables,
} from "_graphql/mutation/kek/__generated__/CreateKEKInputOffer";
import { useMutation, useReactiveVar } from "@apollo/client";

import { StepFormBuilder } from "../../..";
import { createKEKPolicy } from "_graphql/kek/mutation/index.kek";
import { createOffer } from "_graphql/visal/mutations/offers";
import { currentUserVar } from "_graphql/cache/auth";
import { getDomain } from "router/filter";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import { useMemo } from "react";
import useApi from "utils/use-api";
import OfferDetailsForm from "../offer-details-form";
import OfferDetailsSummary from "../offer-details-summary";
import PlacingSlipForm from "../placing-slip-form";
import PlacingSlipSummary from "../placing-slip-summary";
import { FormItem } from "components/core/step-form-builder";
import {
    getPendingPlacingSlipFiles,
    uploadUnsignedPlacingSlip,
} from "../placing-slip-api";
import {
    buildKEKOfferInput,
    buildVisalOfferInput,
} from "../utils";
import { createDetailsInitialValues } from "../validation";

type Props = {
    setClose?: (value: boolean) => void;
    refetch?: () => void;
    onFleetDetected?: (values: any) => void;
};

const CreateSlipWizardContainer = ({
    setClose,
    refetch,
    onFleetDetected,
}: Props) => {
    const api = useApi();
    const user = useReactiveVar(currentUserVar);
    const domain = getDomain();

    const [create] = useMutation<
        CreateInputOffer,
        CreateInputOfferVariables
    >(createOffer, {
        refetchQueries: ["Offers"],
    });

    const [createKEK] = useMutation<
        CreateKEKInputOffer,
        CreateKEKInputOfferVariables
    >(createKEKPolicy, {
        refetchQueries: ["Offers"],
    });

    const formSteps: FormItem[] = useMemo(
        () => [
            {
                accessor: "details",
                description: "Provide details to create a new offer",
                name: "Offer Details",
                FormComponent: (props: any) => (
                    <OfferDetailsForm
                        {...props}
                        onFleetDetected={onFleetDetected}
                        isFirstStep={true}
                    />
                ),
                SummaryComponent: OfferDetailsSummary,
            },
            {
                accessor: "placingSlip",
                description: "Upload placing slip for this offer",
                name: "Upload Placing Slip",
                FormComponent: PlacingSlipForm,
                SummaryComponent: PlacingSlipSummary,
            },
        ],
        [onFleetDetected],
    );

    const form = useFormik<any>({
        initialValues: {
            details: createDetailsInitialValues,
            placingSlip: { pendingFiles: [], hasExisting: false },
            placingSlipBusy: false,
        },
        onSubmit: async (values) => {
            const employeeId = user?.employee?.employee_id ?? "";
            const pendingFiles = getPendingPlacingSlipFiles(values.placingSlip);

            try {
                let offerId: string | undefined;

                if (domain === "KEK") {
                    const { data } = await createKEK({
                        variables: {
                            offerInput: buildKEKOfferInput(
                                values.details,
                                employeeId,
                            ),
                        },
                    });
                    if (!data?.createInputOffer) {
                        toast.error("Failed to create Offer");
                        return;
                    }
                    offerId = data.createInputOffer.offer_id;
                } else {
                    const { data } = await create({
                        variables: {
                            offerInput: buildVisalOfferInput(
                                values.details,
                                employeeId,
                            ),
                        },
                    });
                    if (!data?.createInputOffer?.offer_id) {
                        toast.error("Failed to create Offer");
                        return;
                    }
                    offerId = data.createInputOffer.offer_id;
                }

                if (pendingFiles.length > 0 && offerId) {
                    try {
                        await uploadUnsignedPlacingSlip(
                            api,
                            offerId,
                            pendingFiles[0],
                        );
                    } catch {
                        toast.error(
                            "Offer created but placing slip upload failed",
                        );
                        return;
                    }
                }

                toast.success("Offer Created Successfully");
                refetch?.();
                setClose?.(false);
            } catch {
                toast.error("Failed to create Offer");
            }
        },
    });

    return (
        <StepFormBuilder
            formSteps={formSteps}
            form={form}
            setOpen={setClose}
        />
    );
};

export default CreateSlipWizardContainer;
